import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { LuBuilding, LuPhone, LuMessageSquare, LuBriefcase, LuLock } from 'react-icons/lu';
import { NavigateFunction } from 'react-router-dom';
import {
  addTeamMembers,
  assignClientContact,
  assignClientUser,
  axios,
  deleteTeamMember,
  getAssignedClient,
  getAssignedClientContact,
  getTeamsByUser,
  ITeam,
  revokeClientContact,
  revokeClientUser,
  toggleOrganizationalUnitAccess,
} from 'wisestep-api';
import { WsUsersRound, UserRoundIcon } from 'wisestep-base';

import { IOption } from '../../../../../components/TeamsOptions';
import TeamAvatar from '../../../../../images/Avatar/Group.svg';
import OrgUnit from '../../../../../images/Avatar/OrgUnit.svg';
import PersonAvatar from '../../../../../images/Avatar/Person.svg';
import { ApiFunctionMap, UserAccessTypes } from '../types/userProfileTypes';

export const userAccessNavOptions: IOption[] = [
  {
    label: 'Teams',
    icon: <WsUsersRound width={20} height={20} />,
  },
  {
    label: 'Clients',
    icon: <LuBuilding size={20} />,
  },
  {
    label: 'Client Contacts',
    icon: <UserRoundIcon />,
  },
  {
    label: 'Data Access',
    icon: <LuLock size={20} />,
  },
  {
    label: 'Job Board Accounts',
    icon: <LuBriefcase size={20} />,
  },
  {
    label: 'SMS Number',
    icon: <LuMessageSquare size={20} />,
  },
  {
    label: 'Call Number',
    icon: <LuPhone size={20} />,
  },
  {
    label: 'Client Units',
    icon: <LuBuilding size={20} />,
  },
  {
    label: 'Account Manager Units',
    icon: <LuBuilding size={20} />,
  },
  {
    label: 'Client Contact Units',
    icon: <LuBuilding size={20} />,
  },
  {
    label: 'Delivery Units',
    icon: <LuBuilding size={20} />,
  },
  {
    label: 'Business Units',
    icon: <LuBuilding size={20} />,
  },
];

const toggleAccessForOrgUnits = (
  ids: string[],
  email: string,
  accessType: 'GRANTED' | 'REMOVED'
) => {
  const promises = ids.map((id) =>
    toggleOrganizationalUnitAccess(id, {
      organizationalId: id,
      viewersIds: [email],
      accessType,
    })
  );
  return Promise.allSettled(promises) as unknown as Promise<AxiosResponse>;
};

export const userAccessActionApiMap: Record<'VIEW' | 'DELETE' | 'ADD', ApiFunctionMap> = {
  VIEW: {
    ['Teams']: (email: string, config: AxiosRequestConfig) => getTeamsByUser(email, config),
    ['Clients']: (email: string, config: AxiosRequestConfig) => getAssignedClient(email, config),
    ['Client Contacts']: (email: string, config: AxiosRequestConfig) =>
      getAssignedClientContact(email, config),
    ['Client Units']: (email: string, config: AxiosRequestConfig) =>
      getOrganizationalUnitsByUser(email, 'Clients', config),
    ['Account Manager Units']: (email: string, config: AxiosRequestConfig) =>
      getOrganizationalUnitsByUser(email, 'Account Managers', config),
    ['Client Contact Units']: (email: string, config: AxiosRequestConfig) =>
      getOrganizationalUnitsByUser(email, 'Client Contacts', config),
    ['Delivery Units']: (email: string, config: AxiosRequestConfig) =>
      getOrganizationalUnitsByUser(email, 'Teams', config),
  },
  DELETE: {
    ['Teams']: (id: string, email: string) => deleteTeamMember(email, id),
    ['Clients']: (id: string, email: string) =>
      revokeClientUser({ clientId: id, userList: [email] }),
    ['Client Contacts']: (id: string, email: string) =>
      revokeClientContact({ clientContactId: id, userList: [email], clientId: '' }),
    ['Client Units']: (id: string, email: string) =>
      toggleAccessForOrgUnits([id], email, 'REMOVED'),
    ['Account Manager Units']: (id: string, email: string) =>
      toggleAccessForOrgUnits([id], email, 'REMOVED'),
    ['Client Contact Units']: (id: string, email: string) =>
      toggleAccessForOrgUnits([id], email, 'REMOVED'),
    ['Delivery Units']: (id: string, email: string) =>
      toggleAccessForOrgUnits([id], email, 'REMOVED'),
  },
  ADD: {
    ['Teams']: (ids: string[], email: string) =>
      addTeamMembers({ teamMemberDTOList: ids.map((id) => ({ teamId: id, email })) }),
    ['Clients']: (ids: string[], email: string) =>
      assignClientUser({ clientIds: ids, userList: [email] }),
    ['Client Contacts']: (ids: string[], email: string) =>
      assignClientContact({ clientContactIds: ids, userList: [email], clientId: '' }),
    ['Client Units']: (ids: string[], email: string) =>
      toggleAccessForOrgUnits(ids, email, 'GRANTED'),
    ['Account Manager Units']: (ids: string[], email: string) =>
      toggleAccessForOrgUnits(ids, email, 'GRANTED'),
    ['Client Contact Units']: (ids: string[], email: string) =>
      toggleAccessForOrgUnits(ids, email, 'GRANTED'),
    ['Delivery Units']: (ids: string[], email: string) =>
      toggleAccessForOrgUnits(ids, email, 'GRANTED'),
  },
};

export const getIcons = (
  type: UserAccessTypes
): { src: string; variant: 'rounded' | 'circular' } => {
  switch (type) {
    case 'Teams':
      return { src: TeamAvatar, variant: 'rounded' };
    case 'Client Contacts':
      return { src: PersonAvatar, variant: 'circular' };
    case 'Clients':
    case 'Client Units':
    case 'Account Manager Units':
    case 'Client Contact Units':
    case 'Delivery Units':
    case 'Business Units':
      return { src: OrgUnit, variant: 'rounded' };
    default:
      return { src: TeamAvatar, variant: 'rounded' };
  }
};

export const getNavigationLink = (entity, type: UserAccessTypes): string => {
  switch (type) {
    case 'Teams':
      return `/team/team-members-view?teamId=${entity.id}&teamName=${entity.name}`;
    case 'Clients':
      return `/client-details?name=${entity.name}&isChild=true&clientId=${entity.id}&tabIndex=0`;
    case 'Client Contacts':
      return `/${entity.id}/client-contact-details?name=${entity.clientName}&isChild=true`;
    default:
      return '#';
  }
};

export const navigateToOrgUnit = (orgUnit, navigate: NavigateFunction) => {
  const navigationParams = {
    organizationalUnitDescription: orgUnit.description,
    organizationalUnitName: orgUnit.name,
    organizationalUnitId: orgUnit.id,
    organizationalUnitScopeType: orgUnit.scopeType,
    organizationalUnitOwner: orgUnit.createdBy,
    isSettingsPage: false,
    isClientsOrganizationalUnit: orgUnit.scopeType === 'Clients',
    isClientContactOrganizationalUnit: orgUnit.scopeType === 'Client Contacts',
    clientOrClientContactIds: [orgUnit.id],
  };
  navigate('/organizational_unit/user-view', {
    state: navigationParams,
  });
};

export const removeDuplicates = (arr: ITeam[]) => {
  const uniqueIds = new Set();
  return arr.filter((item) => {
    if (!uniqueIds.has(item.id)) {
      uniqueIds.add(item.id);
      return true;
    }
    return false;
  });
};

export const getOrganizationalUnitsByUser = (
  email: string,
  scopeType: string,
  config?: AxiosRequestConfig
) => axios.get(`/api/organizational-units-by-user?userId=${email}&scopeType=${scopeType}`, config);
